import React, { useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import {
  PdcCurveChart,
  normalizePeakCurve,
  type PeakType,
} from '../../components/charts/PdcCurveChart';
import { useChartLandscapeFullscreen, useFullscreenChartHeight } from '../../hooks/useChartLandscapeFullscreen';
import { fmtWorkoutDayLabel, workoutHasPeakData } from '../../features/workout/useWorkoutPdcChart';
import type { WorkoutDetail } from '../../services/api';
import type { WorkoutSummary } from './types';
import { COMPARISON_COLORS } from './types';

interface Props {
  detailA: WorkoutDetail | null;
  detailB: WorkoutDetail | null;
  workoutA: WorkoutSummary | null;
  workoutB: WorkoutSummary | null;
  chartHeight?: number;
}

function legendForWorkout(
  slot: 'A' | 'B',
  summary: WorkoutSummary | null,
  fallbackA: string,
  fallbackB: string,
  locale: string,
): string {
  if (!summary) return slot === 'A' ? fallbackA : fallbackB;
  const day = summary.workout_day ? fmtWorkoutDayLabel(summary.workout_day, locale) : '';
  const title = summary.title?.trim();
  if (day && title) return `${title} (${day})`;
  return title || day || (slot === 'A' ? fallbackA : fallbackB);
}

/** Peak-duration curves for two selected workouts (workout-detail PDC parity). */
export function ComparisonPdcChart({
  detailA,
  detailB,
  workoutA,
  workoutB,
  chartHeight = 280,
}: Props) {
  const { t, i18n } = useTranslation();
  const [peakType, setPeakType] = useState<PeakType>('pw_peak');
  const { open: fsOpen, enter: enterFs, exit: exitFs, overlayRef } = useChartLandscapeFullscreen();
  const fsChartHeight = useFullscreenChartHeight(fsOpen, overlayRef, 108);

  const peaksA = detailA?.details?.peaks ?? {};
  const peaksB = detailB?.details?.peaks ?? {};

  const currentCurve = useMemo(
    () => normalizePeakCurve(peaksA[peakType] as Record<string, unknown> | undefined),
    [peaksA, peakType],
  );
  const compareCurve = useMemo(
    () => normalizePeakCurve(peaksB[peakType] as Record<string, unknown> | undefined),
    [peaksB, peakType],
  );

  const hasA = workoutHasPeakData(detailA);
  const hasB = workoutHasPeakData(detailB);
  const hasData = currentCurve.length > 0 || compareCurve.length > 0;

  const currentLegendLabel = useMemo(
    () => legendForWorkout('A', workoutA, t('compare.workoutA'), t('compare.workoutB'), i18n.language),
    [workoutA, t, i18n.language],
  );
  const compareLegendLabel = useMemo(
    () => legendForWorkout('B', workoutB, t('compare.workoutA'), t('compare.workoutB'), i18n.language),
    [workoutB, t, i18n.language],
  );

  const dayA = (workoutA?.workout_day ?? '').slice(0, 10);
  const dayB = (workoutB?.workout_day ?? '').slice(0, 10);

  const chartProps = useMemo(
    () => ({
      currentCurve,
      compareCurve,
      compareStart: dayB || dayA,
      compareEnd: dayB || dayA,
      currentLabel: currentLegendLabel,
      peakType,
      onPeakTypeChange: setPeakType,
      onCompareStartChange: () => {},
      onCompareEndChange: () => {},
      currentLegendLabel,
      compareLegendLabel,
      hideCompareControls: true,
      variant: 'summary' as const,
    }),
    [compareCurve, compareLegendLabel, currentCurve, currentLegendLabel, dayA, dayB, peakType],
  );

  if (!workoutA || !workoutB) return null;
  if (!hasA && !hasB && !hasData) {
    return <p style={{ fontSize: 12, color: 'var(--fg-3)', margin: 0 }}>{t('pdcChart.empty')}</p>;
  }

  const inlineChart = (
    <PdcCurveChart {...chartProps} chartHeight={chartHeight} onExpand={enterFs} />
  );

  return (
    <div className="cmp-pdc">
      <div className="cmp-pdc__legend-dots" aria-hidden>
        <span style={{ background: COMPARISON_COLORS.a }} />
        <span style={{ background: COMPARISON_COLORS.b }} />
      </div>
      {inlineChart}
      {fsOpen &&
        createPortal(
          <div ref={overlayRef} className="pdc-chart-fs-overlay" role="dialog" aria-modal="true">
            <div className="pdc-chart-fs-overlay__bar">
              <span className="pdc-chart-fs-overlay__title">{t('compare.meanMaxCurve')}</span>
              <button type="button" className="pdc-chart-fs-overlay__close" onClick={exitFs} aria-label={t('pdcChart.close')}>
                ×
              </button>
            </div>
            <p className="pdc-chart-fs-overlay__hint">{t('pdcChart.rotateHint')}</p>
            <div className="pdc-chart-fs-overlay__body ltr-island">
              <PdcCurveChart
                key={`comparison-pdc-fs-${fsChartHeight}`}
                {...chartProps}
                chartHeight={fsChartHeight}
                layoutMode="fullscreen"
              />
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
}
